import React, { createContext, useContext, useState } from 'react';
import { IntlProvider } from 'react-intl';
import enMessages from '../translations/en.json';
import frMessages from '../translations/fr.json';
import esMessages from '../translations/es.json';
import { LANGUAGE_LOCALE_MAP } from '#root/utils/settings';
import { useCurrentUser } from '#root/queries/local';
import { LOCAL_STORAGE_KEYS } from '#root/utils/localStorage';

// Map of language names to their translation files
const messages = {
  English: enMessages,
  French: frMessages,
  Spanish: esMessages,
} as const;

// Type definition for our language context
type LanguageContextType = {
  currentLanguage: string;
  setLanguage: (language: string) => void;
};

// Create context with a type assertion for the initial empty value
const LanguageContext = createContext<LanguageContextType>({} as LanguageContextType);

// Hook to access language context from any component
export const useLanguage = () => useContext(LanguageContext);

// Props for the local IntlProvider wrapper
type LocalIntlProviderProps = {
  language: string;
  children: React.ReactNode;
};

/**
 * Local IntlProvider that can be used to create language-specific contexts
 * Useful when you want to have different languages in different parts of the app
 */
export const LocalIntlProvider: React.FC<LocalIntlProviderProps> = ({ language, children }) => (
  <IntlProvider
    messages={messages[language as keyof typeof messages]}
    locale={LANGUAGE_LOCALE_MAP[language]}
    defaultLocale="en"
  >
    {children}
  </IntlProvider>
);

/**
 * Main language provider that:
 * 1. Manages the current language state
 * 2. Syncs language with user preferences in localStorage
 * 3. Provides translation context to the entire app
 */
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get current user data from localStorage
  const { data: currentUser } = useCurrentUser();
  
  // Initialize language state from user preferences or default to English
  const [currentLanguage, setCurrentLanguage] = useState(() => 
    currentUser?.language || 'English'
  );

  // Update language in both state and localStorage
  const setLanguage = (language: string) => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY, JSON.stringify({ 
      ...currentUser, 
      language 
    }));
    setCurrentLanguage(language);
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage }}>
      <LocalIntlProvider language={currentLanguage}>
        {children}
      </LocalIntlProvider>
    </LanguageContext.Provider>
  );
};
